import React, { ReactNode } from 'react';

import { type UnifiedTransfer } from '@column/api';
import { type ChipProps, Icon, formatNumber } from '@column/column-ui-kit';
import { cn } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { TransferEntryType } from '~/repositories';
import { formatString } from '~/util';

export type BadgeTone = 'secondary' | 'success' | 'danger' | 'info';

// Transfer search stops counting matches here for dashboard callers, so total_results_count saturates.
export const TRANSFER_COUNT_CAP = 10000;

export const pluralizeTransfers = (count: number): string =>
  `${formatNumber(count, false)} ${count === 1 ? 'transfer' : 'transfers'}`;

export const formatTransferCount = (totalResultsCount: number): string =>
  `${formatNumber(totalResultsCount, false)}${totalResultsCount >= TRANSFER_COUNT_CAP ? '+' : ''}`;

export const isBatchTransfer = (transfer: Pick<UnifiedTransfer, 'type'>): boolean =>
  transfer.type === 'ach_batch' || transfer.type === 'check_batch';

export const getBatchTypeFromId = (batchId: string): 'ach_batch' | 'check_batch' | undefined => {
  if (batchId.startsWith('atbt_')) return 'ach_batch';
  if (batchId.startsWith('tbat_')) return 'check_batch';
  return undefined;
};

export const getBatchNetAmount = (batch: Pick<UnifiedTransfer, 'amount' | 'type'>): number =>
  batch.type === 'ach_batch' ? -(batch.amount ?? 0) : (batch.amount ?? 0);

export const isUnsentAchBatch = (batch: Pick<UnifiedTransfer, 'status' | 'type'>): boolean =>
  batch.type === 'ach_batch' && ['pending', 'validating', 'validated'].includes(batch.status);

export const getAchBatchEditURL = (batchId: string): string =>
  `${ROUTE.TRANSFER_BATCHES}/edit/ach-batch?batchId=${encodeURIComponent(batchId)}`;

export const isBatchAmountUnavailable = (batch: Pick<UnifiedTransfer, 'status' | 'type'>): boolean =>
  batch.type === 'ach_batch' && ['pending', 'validating', 'canceled', 'expired'].includes(batch.status);

export const getTransferChipType = (tone: BadgeTone = 'secondary'): ChipProps['type'] =>
  tone === 'secondary' ? 'default' : tone;

export const getTransferChipClassName = (tone: BadgeTone = 'secondary') =>
  cn(
    '[&_svg]:[--icon-color:currentColor]',
    tone === 'secondary' && '![--chip-color:var(--color-muted-foreground)]',
    tone === 'success' && '![--chip-color:var(--color-seafoam-600)]',
    tone === 'danger' && '![--chip-color:var(--color-red-500)]',
    tone === 'info' && '![--chip-color:var(--color-blue-500)]'
  );

export const getTransferBadgeClassName = (tone: BadgeTone = 'secondary') =>
  cn(
    'gap-1 bg-transparent pl-0 py-0 text-xs font-medium leading-4 shadow-none hover:bg-transparent [a&]:hover:bg-transparent [a&]:hover:text-current [&>svg]:[--icon-size-x:16px] [&>svg]:[--icon-size-y:16px] [&>svg]:[--icon-size:16px] [&>svg]:shrink-0',
    tone === 'secondary' && 'text-muted-foreground',
    tone === 'success' && 'text-seafoam-600 dark:text-seafoam-600',
    tone === 'danger' && 'text-red-500 dark:text-red-500',
    tone === 'info' && 'text-blue-500 dark:text-blue-400'
  );

export const getCountChipClassName = (tone: BadgeTone = 'secondary') =>
  cn(
    'h-5 min-w-5 shrink-0 justify-center rounded-md border-transparent px-[5px] py-0.5 text-xs font-medium leading-[14px] tabular-nums',
    tone === 'secondary' && 'bg-muted text-muted-foreground',
    tone === 'success' && 'bg-seafoam-600/10 text-seafoam-600',
    tone === 'danger' && 'bg-red-500/10 text-red-500',
    tone === 'info' && 'bg-blue-500/10 text-blue-500 dark:text-blue-400'
  );

const normalizeTransferType = (transferType: string): string => {
  const normalized = String(transferType).toLowerCase().trim();
  if (normalized === 'administrator' || normalized === 'administrative') {
    return 'admin';
  }
  return normalized;
};

export const formatTransferType = (
  transferType?: TransferEntryType,
  { abbreviated }: { abbreviated: boolean } = { abbreviated: true }
) => {
  if (!transferType) {
    return '';
  }

  const normalizedType = normalizeTransferType(String(transferType)) as TransferEntryType;

  switch (normalizedType) {
    case 'ach':
      return 'ACH';
    case 'book':
      return 'Book';
    case 'ach_batch':
      return 'ACH Batch';
    case 'ach_credit':
      return 'ACH Credit';
    case 'ach_debit':
      return 'ACH Debit';
    case 'check_batch':
      return 'Check Batch';
    case 'check_credit':
    case 'check_debit':
      return 'Check';
    case 'swift':
      return abbreviated ? 'Intl. Wire' : 'International Wire';
    case 'wire-drawdown':
      return 'Wire Drawdown';
    case 'admin':
      return 'Admin';
    case 'crypto':
      return 'Stablecoin';
    default:
      return formatString(transferType);
  }
};

export const getTransferIcon = (transferType: TransferEntryType | string) => {
  const normalized = normalizeTransferType(String(transferType)).replace(/\s+/g, '_');
  const iconMap: Record<string, ReactNode> = {
    ach_batch: <Icon.Transfer.ACH />,
    ach_credit: <Icon.Transfer.ACH />,
    ach_debit: <Icon.Transfer.ACH />,
    ach: <Icon.Transfer.ACH />,
    admin: <Icon.Transfer.Admin />,
    book: <Icon.Transfer.Book />,
    check_batch: <CheckScannerIcon className="[&_svg]:[--icon-size:16px]!" />,
    check_credit: <Icon.Transfer.Check />,
    check_debit: <Icon.Transfer.Check />,
    check: <Icon.Transfer.Check />,
    wire: <Icon.Transfer.Wire />,
    'wire-drawdown': <Icon.Transfer.Wire />,
    swift: <Icon.Transfer.InternationalWire />,
    intl_wire: <Icon.Transfer.InternationalWire />,
    realtime: <Icon.Transfer.Realtime />,
    crypto: <Icon.Transfer.Stablecoin />,
  };

  return iconMap[normalized] || <Icon.Transfer.Wire />;
};

export const BATCH_SHEET_PARAM = 'batch';

export const getBatchSheetURL = (batchId: string): string =>
  `${ROUTE.TRANSFERS}?${BATCH_SHEET_PARAM}=${encodeURIComponent(batchId)}`;

export const getTransferURL = (transferId: string, transferType: TransferEntryType): string => {
  switch (transferType) {
    case 'ach_batch':
      return `${ROUTE.TRANSFERS}/batches/ach/${transferId}`;
    case 'check_batch':
      return getBatchSheetURL(transferId);
    case 'ach_credit':
    case 'ach_debit':
      return `${ROUTE.TRANSFERS}/ach/${transferId}`;
    case 'book':
      return `${ROUTE.TRANSFERS}/book/${transferId}`;
    case 'check_credit':
    case 'check_debit':
      return `${ROUTE.TRANSFERS}/check/${transferId}`;
    case 'realtime':
      return `${ROUTE.TRANSFERS}/realtime/${transferId}`;
    case 'swift':
      return `${ROUTE.TRANSFERS}/intl-wire/${transferId}`;
    case 'wire':
      return `${ROUTE.TRANSFERS}/wire/${transferId}`;
    case 'wire-drawdown':
      return `${ROUTE.TRANSFERS}/wire/drawdown/${transferId}`;
    case 'admin':
      return `${ROUTE.TRANSFERS}/admin/${transferId}`;
    case 'crypto':
      return `${ROUTE.TRANSFERS}/stablecoin/${transferId}`;
    default:
      return `${ROUTE.TRANSFERS}/wire/${transferId}`;
  }
};
